import { Button, PopoverBody } from '@chakra-ui/react';
import { BlobProvider } from '@react-pdf/renderer';
import { SecurityOverviewPDF } from './PDFComponents';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import {
  IGetDetectionPeriodSummary,
  IGetDetectionMitreSummary,
} from '../../../detections/queries';
import { i18n } from '@investigator/shared/localization';

const { pdf: pdfLocales } = i18n.pages.securityOverview;

type AlertCategoriesData = {
  categories: { severity: number; name: string; detectionCount: number }[];
  totalItems: number;
};

interface PDFDownloadPopoverContentProps {
  summaryLoading: boolean;
  mitreLoading: boolean;
  summaryError?: ApolloError;
  mitreError?: ApolloError;
  summaryData?: IGetDetectionPeriodSummary;
  mitreData?: IGetDetectionMitreSummary;
  alertCategoriesData: AlertCategoriesData | null;
  highRiskData: {
    summaries: {
      severity: number;
      entity_name: string;
      detection_count: number;
      entity_category: string;
      entity_type: string;
      total_detections: number;
      is_custom_severity: boolean;
    }[];
    total_items: number;
  } | null;
  start: number;
  end: number;
}

export const PDFDownloadPopoverContent = ({
  summaryLoading,
  mitreLoading,
  summaryError,
  mitreError,
  summaryData,
  mitreData,
  alertCategoriesData,
  highRiskData,
  start,
  end,
}: PDFDownloadPopoverContentProps) => {
  // Transform MITRE data for PDF
  const pdfMitreData = useMemo(() => {
    return (mitreData?.getDetectionMitreSummary || []).map((tactic) => ({
      name: tactic.mitre_attack_tactic,
      techniques: tactic.mitre_attack_techniques.map((technique) => ({
        name: technique.mitre_attack_technique,
        count: technique.entity_count,
      })),
    }));
  }, [mitreData]);

  // Transform high risk data for PDF
  const transformedHighRiskData = useMemo(() => {
    if (!highRiskData) return null;
    return {
      getTopDetectionEntitiesPaginated: {
        summaries: highRiskData.summaries.map((summary) => ({
          ...summary,
          entity_id: summary.entity_name,
          tenant: '',
          tenant_entity: '',
          tenant_info: {
            tenant_id: '',
            tenant_display_name: '',
            __typename: 'TenantInfo',
          },
          first_seen: 0,
          last_seen: 0,
          __typename: 'DetectionSummary',
        })),
        total_items: highRiskData.total_items,
        offset: 0,
        size: 10,
        __typename: 'PaginatedDetectionSummaries',
      },
    };
  }, [highRiskData]);

  const isLoading = summaryLoading || mitreLoading;
  const isError = Boolean(summaryError || mitreError);

  return (
    <PopoverBody>
      {isLoading || isError ? (
        <GraphQLReqStatus
          loading={isLoading}
          error={summaryError || mitreError}
        />
      ) : summaryData?.getDetectionPeriodSummary &&
        alertCategoriesData &&
        highRiskData &&
        start &&
        end ? (
        <BlobProvider
          document={
            <SecurityOverviewPDF
              data={summaryData}
              start={start}
              end={end}
              highRiskData={transformedHighRiskData}
              alertCategories={alertCategoriesData.categories}
              alertCategoriesTotal={alertCategoriesData.totalItems}
              mitreData={pdfMitreData}
            />
          }
        >
          {({ url, loading: pdfLoading, error: pdfError }) => {
            if (pdfError) {
              console.error('PDF generation error:', pdfError);
              return (
                <Button
                  disabled
                  colorScheme="red"
                  width="full"
                  title={pdfError.message}
                >
                  {pdfLocales.error}
                </Button>
              );
            }
            return (
              <Button
                as="a"
                href={url || '#'}
                download="security-overview.pdf"
                isLoading={pdfLoading}
                disabled={pdfLoading}
                width="full"
              >
                {pdfLocales.download}
              </Button>
            );
          }}
        </BlobProvider>
      ) : (
        <Button disabled width="full">
          {pdfLocales.waitingForData}
        </Button>
      )}
    </PopoverBody>
  );
};
